<template>
    <v-container fluid>
        <v-card class="elevation-3" :loading="loading">
            <div class="card-title">{{ $t('Dictionary') }}</div>
            <v-card-title>
                <v-text-field v-model="search" clearable append-icon="mdi-magnify" :label="$t('Search')" single-line hide-details />
            </v-card-title>
            <v-data-table :search="search" :headers="headers" dense :items="result" :items-per-page="20" :footer-props="footerProps">
                <template v-slot:top>
                    <v-btn small class="ma-4 white--text" tile color="primary" @click="open()">
                        {{ $t('Add') }} <v-icon small class="ms-2">mdi-plus</v-icon>
                    </v-btn>
                </template>
                <template v-slot:[`item.options`]="{ item }">
                    <v-icon color="primary" class="ms-3" @click="editItem(item)"> mdi-pen</v-icon>
                    <v-icon color="error" class="ms-3" @click="deleteItem(item.id)">mdi-trash-can-outline</v-icon>
                </template>
            </v-data-table>
            <v-form ref="dictionaryForm" v-model="valid" lazy-validation>
                <CommonDialog v-model="dialog" :fullscreen="false" width="500" :title="title" show-action-toolbar>
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field v-model="dictionary.keyword" :label="$t('Keyword')" :rules="nameRules" required />
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="dictionary.ar" :label="$t('NameAr')" :rules="nameRules" required />
                            </v-col>
                            <v-col cols="12">
                                <v-text-field v-model="dictionary.en" :label="$t('NameEn')" :rules="nameRules" required />
                            </v-col>
                        </v-row>
                    </v-container>

                    <template v-slot:actions>
                        <v-btn v-if="!edit" color="primary" class="white--text" tile :disabled="loading" small @click="save()">
                            {{ $t("Add") }}
                        </v-btn>
                        <v-btn v-if="edit" color="primary" class="white--text" tile :disabled="loading" small @click="update()">
                            {{ $t("Update") }}
                        </v-btn>
                        <v-btn color="error" class="white--text" tile :disabled="loading" small @click="dialog = false; reset()">
                            {{ $t("Cancel") }}
                        </v-btn>
                    </template>
                </CommonDialog>
            </v-form>
        </v-card>
    </v-container>
</template>

<script>
    import CommonDialog from '@/components/controls/CommonDialog'
    import DictionaryService from '@/services/DictionaryService.js'

    export default {
        components: {
            CommonDialog
        },

        data() {
            return {
                title: '',
                search: '',
                valid: true,
                loading: false,
                dialog: false,
                edit: false,
                selectedItemId: null,
                result: [],
                headers: [
                    { text: this.$t('Id'), value: 'id' },
                    { text: this.$t('Keyword'), value: 'keyword' },
                    { text: this.$t('TitleAr'), value: 'ar' },
                    { text: this.$t('TitleEn'), value: 'en' },
                    { text: this.$t('Options'), value: 'options', width: '8%' }
                ],
                nameRules: [
                    v => !!v || this.$t('RequiredField')
                ],
                footerProps: {
                    'items-per-page-options': [20, 50, 100],
                    'disable-pagination': false
                },
                dictionary: {
                    keyword: '',
                    ar: '',
                    en: '',
                }
            }
        },

        created() {
            this.loadDictionary()
        },

        methods: {

            // The API answers business failures with HTTP 200 + { success:false, message },
            // and genuine faults with a non-2xx carrying the same envelope. Both used to be
            // collapsed into the generic "ErrorOccurred", which hid what actually happened.
            errorMessage(error) {
                const payload = error && error.response ? error.response.data : null

                if (payload && payload.message) {
                    return payload.message
                }

                if (error && error.response) {
                    if (error.response.status === 404) {
                        return this.$t('NoItemsToShow')
                    }
                    if (error.response.status === 403 || error.response.status === 401) {
                        return this.$te('Unauthorized')
                            ? this.$t('Unauthorized')
                            : (this.$i18n.locale === 'en' ? 'You are not allowed to perform this action.' : 'ليس لديك صلاحية لتنفيذ هذا الإجراء.')
                    }
                }

                return this.$t('ErrorOccurred')
            },

            notifyError(error) {
                this.$root.$notification(this.errorMessage(error), 'error')
            },

            loadDictionary() {
                this.loading = true
                DictionaryService.listDictionary().then((res) => {
                    if (res.data) {
                        this.result = res.data
                    }
                }).catch((error) => {
                    this.notifyError(error)
                }).finally(() => {
                    this.loading = false
                })
            },

            save() {
                if (this.$refs.dictionaryForm.validate()) {
                    DictionaryService.addDictionary(this.dictionary).then((res) => {
                        if (res && res.success === false) {
                            this.$root.$notification(res.message || this.$t('ErrorOccurred'), 'error')
                            return
                        }
                        this.loadDictionary()
                        this.reset()
                        this.$refs.dictionaryForm.resetValidation()
                    }).catch((error) => {
                        this.notifyError(error)
                    }).finally(() => {
                        this.loading = false
                    })
                }
            },

            update() {
                if (this.$refs.dictionaryForm.validate()) {
                    DictionaryService.updateDictionary(this.selectedItemId, this.dictionary).then((res) => {
                        if (res && res.success === false) {
                            this.$root.$notification(res.message || this.$t('ErrorOccurred'), 'error')
                            return
                        }
                        this.dialog = false
                        this.loadDictionary()
                        this.reset()
                    }).catch((error) => {
                        this.notifyError(error)
                    }).finally(() => {
                        this.loading = false
                    })
                }
            },

            async deleteItem(id) {
                if (await this.$root.$confirm(this.$t('Delete'), this.$t('ConfirmDelete'))) {
                    this.loading = true
                    DictionaryService.deleteDictionary(id)
                        .then((res) => {
                            // a refused delete (referenced elsewhere, already removed) comes
                            // back as success:false with a message explaining why
                            if (res && res.success === false) {
                                this.$root.$notification(res.message || this.$t('ErrorOccurred'), 'error')
                                this.loadDictionary()
                                return
                            }
                            this.$root.$notification(this.$t('DeletedSuccessfully'), 'success')
                            this.loadDictionary()
                        })
                        .catch((error) => {
                            this.notifyError(error)
                        }).finally(() => this.loading = false)
                }
            },

            reset() {
                this.dictionary.ar = ''
                this.dictionary.en = ''
                this.dictionary.keyword = ''
                this.selectedItemId = null
            },

            open() {
                this.reset()
                this.edit = false
                this.selectedItemId = null
                this.$refs.dictionaryForm.resetValidation()
                this.title = this.$t('Add')
                this.dialog = true
            },

            editItem(item) {
                this.edit = true
                this.dictionary.keyword = item.keyword
                this.dictionary.ar = item.ar
                this.dictionary.en = item.en
                this.selectedItemId = item.id
                this.title = this.$t('Edit')
                this.dialog = true
            },
        },
    }
</script>

<style>
</style>